import { DynamicDataInterceptor } from './shared-components/has-dynamic-data/dynamic-data.interceptor';
import { LoginModule } from './shared-modules/login/login.module';
import { NewFamilyRegistrationGuard } from './shared-components/guards/new-family-registration/new-family-registration.guard';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { httpInterceptorProviders } from '.';
import { SharedComponentsModule } from './shared-components/shared-components.module';
import { AuthGuard } from './auth.guard';
import { AppComponent } from './app.component';
import { LoginService } from './shared-modules/login/login.service';
import { HeaderComponent } from './shared-components/header/header.component';
import { UserService } from './shared-components/user/user.service';
import { YearService } from './shared-components/year/year.service';
import { MaterialModule } from './shared-components/angular-material/material.module';
import { StudentService } from './shared-components/student/student.service';
import { UserPreferencesService } from './shared-components/user-preferences/user-preferences.service';
import { UnsavedChangesMessageComponent } from './shared-components/guards/unsaved-changes/unsaved-changes-message/unsaved-changes-message.component';
import { UnsavedChangesGuard } from './shared-components/guards/unsaved-changes/unsaved-changes.guard';
import { TranslateModule, TranslateLoader, TranslateService, MissingTranslationHandler } from '@ngx-translate/core';
import { Student360Service } from './student360/student360.service';
import { SharedService } from './shared-components/shared.service';
import { Classroom360Service } from './classroom360/classroom360.service';
import { LoaderService } from './shared-components/spinner/loader-service.component';
import { LoaderInterceptor } from './shared-components/spinner/loader-intercept.component';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { CheckForErrorsGuard } from './shared-components/guards/check-for-errors/check-for-errors.guard';
import { CheckForErrorsMessageComponent } from './shared-components/guards/check-for-errors/check-for-errors-message/check-for-errors-message.component';
import { ReportingModule } from './shared-modules/reporting/reporting.module';
import { ReportingService } from './shared-modules/reporting/reporting.service';
import { UserPreferencesWindowComponent } from './shared-components/user-preferences/user-preferences-window/user-preferences-window.component';
import { ClassroomSummaryService } from './classroom360/classroom-summary/classroom-summary.service';
import { StartOverDialogComponent } from './shared-components/startover-dialog/start-over-dialog.component';
import { OrderModule } from 'ngx-order-pipe';
import { ExportService } from './shared-components/export.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { SecurityService } from './shared-components/security/security.service';
import { MyMissingTranslationHandler } from './shared-modules/translation/missing-translation-handler.component';
import { LogoutComponent } from './shared-components/logout/logout.component';
import { PDFExportModule } from '@progress/kendo-angular-pdf-export';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { ErrorsHandler } from './error-handler';
import { SchedulingTabComponent } from './shared-components/user-preferences/user-preferences-window/scheduling-tab/scheduling-tab.component';
import { AuthService } from './shared-modules/login/auth.service';
import { forkJoin } from 'rxjs';

export function createTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

export function initializeAppFactory(
    loginService: LoginService
  ): () => Promise<void> {
    return () => {
      return new Promise((resolve, reject) => {
            forkJoin([loginService.getOIDCSettings(), loginService.getDistrictSettings()]).subscribe((settings) => {
                loginService.OidcSettings = settings[0];
                loginService.LoginSettings = settings[1];
          // TODO add any work that needs to be done before angular app boostrraps
          resolve();
        });
      });
    };
  }


@NgModule({
    declarations: [
        AppComponent,
        HeaderComponent,
        UnsavedChangesMessageComponent,
        CheckForErrorsMessageComponent,
        UserPreferencesWindowComponent,
        SchedulingTabComponent,
        StartOverDialogComponent,
        LogoutComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        MaterialModule,
        FormsModule,
        HttpClientModule,
        SharedComponentsModule.forRoot(),
        ReportingModule,
        LoginModule,
        OrderModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpClient]
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: MyMissingTranslationHandler
            }
        }),
        PDFExportModule,
        DropDownsModule,
        // Routing Module needs to be last
        AppRoutingModule
    ],
    providers: [
        AuthService,
        LoginService,
        ClassroomSummaryService,
        httpInterceptorProviders,
        UserService,
        YearService,
        AuthGuard,
        UnsavedChangesGuard,
        StudentService,
        UserPreferencesService,
        TranslateService,
        Student360Service,
        SharedService,
        ExportService,
        ReportingService,
        Classroom360Service,
        LoaderService,
        NewFamilyRegistrationGuard,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoaderInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: DynamicDataInterceptor,
            multi: true
        },
        {
            provide: ErrorHandler,
            useClass: ErrorsHandler,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeAppFactory,
            deps: [LoginService],
            multi: true,
        },
        // { provide: APP_BASE_HREF, useFactory: getBaseLocation }
        CheckForErrorsGuard,
        { provide: MatDialogRef, useValue: {} },
        { provide: MAT_DIALOG_DATA, useValue: [] },
        SecurityService
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}
